import { useSyncExternalStore } from "react";

export type ThemePreference = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

// Also read by the inline bootstrap script in templates/index.html.
const STORAGE_KEY = "shift:theme";

const listeners = new Set<() => void>();
const systemDark = window.matchMedia("(prefers-color-scheme: dark)");

function readPreference(): ThemePreference {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "light" || stored === "dark" || stored === "system") return stored;
  } catch {
    // Storage blocked — fall through to the default.
  }
  return "dark";
}

let preference = readPreference();

function resolve(pref: ThemePreference): ResolvedTheme {
  if (pref === "system") return systemDark.matches ? "dark" : "light";
  return pref;
}

function apply() {
  document.documentElement.classList.toggle("dark", resolve(preference) === "dark");
  listeners.forEach((listener) => listener());
}

systemDark.addEventListener("change", () => {
  if (preference === "system") apply();
});

// Another tab changed the theme.
window.addEventListener("storage", (event) => {
  if (event.key !== STORAGE_KEY) return;
  preference = readPreference();
  apply();
});

export function setThemePreference(next: ThemePreference) {
  preference = next;
  try {
    localStorage.setItem(STORAGE_KEY, next);
  } catch {
    // Storage blocked — the choice lasts for this page load only.
  }
  apply();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useThemePreference(): ThemePreference {
  return useSyncExternalStore(subscribe, () => preference);
}

export function useResolvedTheme(): ResolvedTheme {
  return useSyncExternalStore(subscribe, () => resolve(preference));
}
